/**
 * Helpers for turning dropped / picked files and folders into a flat list of
 * jpegs. Only the immediate children of a folder are collected (no recursion)
 * so that camera trap sub-folders with duplicate file names (e.g.
 * 100RECNX/IMG_0001.JPG and 101RECNX/IMG_0001.JPG) don't collide.
 */

// minimal typings for the File and Directory Entries API (not in TS 3.4 lib)
interface FileSystemEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
}

interface FileSystemFileEntryLike extends FileSystemEntryLike {
  file: (success: (file: File) => void, error?: (e: Error) => void) => void;
}

interface FileSystemDirectoryEntryLike extends FileSystemEntryLike {
  createReader: () => {
    readEntries: (
      success: (entries: FileSystemEntryLike[]) => void,
      error?: (e: Error) => void,
    ) => void;
  };
}

/**
 * only accept jpegs, and skip hidden files such as the macOS "._IMG_0001.JPG"
 * metadata files that are common on camera SD cards
 */
export function isJpeg(file: File) {
  return file.type === 'image/jpeg' && !file.name.startsWith('.');
}

function fileFromEntry(entry: FileSystemFileEntryLike): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/**
 * readEntries returns results in batches (100 at a time in chrome), so keep
 * reading until an empty batch is returned
 */
function readAllEntries(
  dir: FileSystemDirectoryEntryLike,
): Promise<FileSystemEntryLike[]> {
  const reader = dir.createReader();
  const entries: FileSystemEntryLike[] = [];
  return new Promise((resolve, reject) => {
    const next = () => {
      reader.readEntries(batch => {
        if (!batch.length) {
          resolve(entries);
          return;
        }
        entries.push(...batch);
        next();
      }, reject);
    };
    next();
  });
}

async function filesFromEntry(entry: FileSystemEntryLike): Promise<File[]> {
  if (entry.isFile) {
    return [await fileFromEntry(entry as FileSystemFileEntryLike)];
  }
  if (entry.isDirectory) {
    const children = await readAllEntries(
      entry as FileSystemDirectoryEntryLike,
    );
    // immediate children only - sub-folders are ignored
    return Promise.all(
      children
        .filter(child => child.isFile)
        .map(child => fileFromEntry(child as FileSystemFileEntryLike)),
    );
  }
  return [];
}

/**
 * Collect the jpegs from a drop event. Dropped files are used as-is and
 * dropped folders contribute their immediate jpeg children.
 */
export async function collectDroppedFiles(
  dataTransfer: DataTransfer,
): Promise<File[]> {
  const items = Array.from(dataTransfer.items || []);
  if (!items.length || typeof items[0].webkitGetAsEntry !== 'function') {
    // entries api not supported - fall back to the plain file list
    return Array.from(dataTransfer.files).filter(isJpeg);
  }

  // entries must be read synchronously, the data transfer is emptied as soon
  // as the drop handler yields
  const entries: FileSystemEntryLike[] = items
    .filter(item => item.kind === 'file')
    .map(item => item.webkitGetAsEntry())
    .filter(entry => !!entry);

  const files = await Promise.all(entries.map(filesFromEntry));
  return ([] as File[]).concat(...files).filter(isJpeg);
}

/**
 * Collect the jpegs from a folder picked with a `webkitdirectory` input.
 * The input returns every nested file, so keep only those directly inside the
 * picked folder (webkitRelativePath is "folder/file.jpg").
 */
export function collectPickedFolderFiles(files: FileList): File[] {
  return Array.from(files).filter(f => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const path: string = (f as any).webkitRelativePath || f.name;
    return path.split('/').length <= 2 && isJpeg(f);
  });
}
